import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useEduAuth } from '../../context/EduAuthContext';
import { ArrowLeft, CheckCircle, Zap, Clock, BookOpen } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/edu`;

export default function CoursePage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { eduUser } = useEduAuth();
  const contentRef = useRef(null);

  const [course, setCourse] = useState(null);
  const [lesson, setLesson] = useState(null);
  const [loading, setLoading] = useState(true);
  const [completed, setCompleted] = useState(false);
  const [completing, setCompleting] = useState(false);

  const authHeader = { Authorization: `Bearer ${eduUser?.token}` };

  useEffect(() => {
    axios.get(`${API}/courses/${slug}`, { headers: authHeader })
      .then(({ data }) => {
        const c = data.data;
        setCourse(c);
        const firstLesson = c.modules?.[0]?.lessons?.[0] || null;
        setLesson(firstLesson);
        if (firstLesson) {
          setCompleted((c.completedLessonIds || []).includes(firstLesson.id));
        }
        axios.post(`${API}/enroll/${c.id}`, {}, { headers: authHeader }).catch(() => {});
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [slug]);

  // Inject copy buttons into every <pre> block after content renders
  useEffect(() => {
    if (!contentRef.current || !lesson) return;
    const blocks = contentRef.current.querySelectorAll('pre');
    blocks.forEach((block) => {
      if (block.dataset.copyInjected) return;
      block.dataset.copyInjected = 'true';

      const btn = document.createElement('button');
      btn.innerHTML = 'Copy';
      btn.style.cssText = [
        'position:absolute', 'top:10px', 'right:10px',
        'background:rgba(168,85,247,0.18)', 'color:#c4b5fd',
        'border:1px solid rgba(168,85,247,0.35)', 'border-radius:6px',
        'padding:5px 14px', 'font-size:12px', 'font-weight:600',
        'cursor:pointer', 'font-family:inherit',
        'transition:all 0.18s', 'z-index:2',
        '-webkit-tap-highlight-color:transparent',
        'min-width:64px', 'text-align:center',
      ].join(';');

      btn.ontouchstart = () => {};
      btn.onclick = () => {
        const text = (block.querySelector('code')?.innerText || block.innerText).trim();
        navigator.clipboard.writeText(text).then(() => {
          btn.innerHTML = '✓ Copied!';
          btn.style.color = '#4ade80';
          btn.style.background = 'rgba(74,222,128,0.12)';
          btn.style.borderColor = 'rgba(74,222,128,0.35)';
          setTimeout(() => {
            btn.innerHTML = 'Copy';
            btn.style.color = '#c4b5fd';
            btn.style.background = 'rgba(168,85,247,0.18)';
            btn.style.borderColor = 'rgba(168,85,247,0.35)';
          }, 2200);
        });
      };

      block.style.position = 'relative';
      block.appendChild(btn);
    });
  }, [lesson]);

  const handleComplete = async () => {
    if (!lesson || completed) return;
    setCompleting(true);
    try {
      await axios.post(`${API}/progress/complete`, { lessonId: lesson.id }, { headers: authHeader });
      setCompleted(true);
    } catch (e) {
      console.error(e);
    } finally {
      setCompleting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0d0d10] flex items-center justify-center">
        <div className="w-6 h-6 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!course || !lesson) {
    return (
      <div className="min-h-screen bg-[#0d0d10] flex flex-col items-center justify-center gap-3 text-gray-400 px-4">
        <BookOpen className="w-10 h-10 text-gray-600" />
        <p>Course not found</p>
        <button onClick={() => navigate('/edu')} className="text-sm text-purple-400 hover:text-purple-300">
          ← Back to courses
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0d0d10] text-white flex flex-col">

      {/* Sticky top bar */}
      <div className="sticky top-0 z-20 border-b border-white/5 bg-[#0d0d10]/90 backdrop-blur-md px-4 py-3 flex items-center gap-3">
        <button
          data-testid="back-to-courses-btn"
          onClick={() => navigate('/edu')}
          className="flex items-center gap-1.5 text-sm text-gray-400 hover:text-white transition-colors flex-shrink-0 -ml-1 p-1"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Courses</span>
        </button>

        <div className="flex-1 min-w-0" />

        <div className="flex items-center gap-2 flex-shrink-0">
          {course.isFree && (
            <span className="flex items-center gap-1 text-xs text-green-400 bg-green-500/10 border border-green-500/20 px-2.5 py-1 rounded-full font-medium">
              <Zap className="w-3 h-3" /> Free
            </span>
          )}
          <span className="flex items-center gap-1 text-xs text-gray-500 bg-white/5 px-2.5 py-1 rounded-full">
            <Clock className="w-3 h-3" /> {lesson.duration} min
          </span>
        </div>
      </div>

      {/* Main scrollable content */}
      <main className="flex-1 w-full max-w-2xl mx-auto px-4 pt-6 pb-10 sm:px-6">

        {/* Course title & description */}
        <div className="mb-7">
          <p className="text-xs font-semibold text-purple-400 uppercase tracking-widest mb-2">Vibe Coding</p>
          <h1 className="text-xl sm:text-2xl font-bold text-white leading-snug mb-3">{course.title}</h1>
          <p className="text-sm text-gray-400 leading-relaxed">{course.description}</p>
        </div>

        {/* Completion badge (shown if already done) */}
        {completed && (
          <div className="flex items-center gap-2 text-sm text-green-400 bg-green-500/8 border border-green-500/15 rounded-xl px-4 py-2.5 mb-6">
            <CheckCircle className="w-4 h-4 flex-shrink-0" />
            <span className="font-medium">You completed this lesson</span>
          </div>
        )}

        {/* Lesson content */}
        <div
          data-testid="lesson-content"
          ref={contentRef}
          className="lesson-content"
          dangerouslySetInnerHTML={{ __html: lesson.content }}
        />

        {/* Mark complete */}
        <div className="mt-10 pt-6 border-t border-white/5">
          <button
            data-testid="mark-complete-btn"
            onClick={handleComplete}
            disabled={completed || completing}
            className={`w-full flex items-center justify-center gap-2 px-6 py-4 rounded-2xl text-base font-semibold transition-all ${
              completed
                ? 'bg-green-500/10 text-green-400 border border-green-500/20 cursor-default'
                : 'bg-gradient-to-r from-pink-600 to-purple-600 text-white shadow-lg shadow-purple-900/30 active:scale-[0.98]'
            } disabled:opacity-60`}
          >
            {completed ? (
              <><CheckCircle className="w-5 h-5" /> Lesson Complete — Nice work!</>
            ) : completing ? (
              <div className="w-5 h-5 border-2 border-white/40 border-t-white rounded-full animate-spin" />
            ) : (
              <><CheckCircle className="w-5 h-5" /> Mark as Complete</>
            )}
          </button>
        </div>

      </main>
    </div>
  );
}
